/**
 * Which operators a column's data type accepts — the grammar's answer to "what can I ask of this
 * column", and the ONE place it is written down. The editor's dropdowns are built from this table
 * (`ui/.../lib/operators.ts` supplies only the labels and value-controls), and `operator-fit.ts`
 * validates a saved definition against it.
 *
 * Read this as the PRODUCT's grammar, not the warehouse's. Several combinations left out here are
 * legal SQL — `gt` on a string column compares lexicographically in every dialect we support — but an
 * audience rule that asks whether a plan name is "greater than" another is far more often a mistake
 * than an intention, and the `rawSql` escape hatch exists for the genuine cases. What the table must
 * never do is admit something that CANNOT compile, which is why the window operators are timestamp-only.
 *
 * `in`/`nin` are listed for every type, including `timestamp`: they are `arity: 'list'` with no type
 * constraint, `col IN (?, ?)` compiles in every dialect, and such rules were savable before this table
 * existed. Refusing them would be a regression with no `rawSql` remedy under
 * `AUDIENCE_EXPRESSION_MODE=strict`, which rejects every raw fragment.
 *
 * Shared leaf: imports one type and the operator names, nothing else, so the browser can read it.
 */

import type { AttributeDataType } from '../data-type.js';
import type { OperatorName } from './names.js';

/** Presence — IS NOT NULL / IS NULL. Meaningful for every type, so every set ends with it. */
const PRESENCE = ['nnull', 'null'] as const satisfies readonly OperatorName[];

export const OPERATORS_BY_TYPE = {
  string: ['eq', 'neq', 'like', 'nlike', 'in', 'nin', ...PRESENCE],
  number: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'in', 'nin', 'btw', ...PRESENCE],
  boolean: ['eq', 'neq', ...PRESENCE],
  timestamp: ['inlast', 'notinlast', 'innext', 'btw', 'eq', 'neq', 'lt', 'lte', 'gt', 'gte', 'in', 'nin', ...PRESENCE],
} as const satisfies Record<AttributeDataType, readonly OperatorName[]>;

/** Whether `operator` is in `type`'s set. An unknown type is not a member of the record's key set, so
 *  callers must pass a resolved `AttributeDataType` — "the column type is unknown" is the CALLER's
 *  decision to skip the check, never a silent `false` here. */
export function isOperatorAllowedForType(type: AttributeDataType, operator: OperatorName): boolean {
  return (OPERATORS_BY_TYPE[type] as readonly OperatorName[]).includes(operator);
}
